import { PencilSimple, CheckCircle, ArrowCounterClockwise } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

const fmtInr = (n) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/**
 * Adjust-Charge dialog. Lets Owner override the consultancy charge for Fired/Left candidates.
 * Presentational — parent owns form state, submit, and clear handlers.
 */
export const AdjustChargeDialog = ({
  open, onOpenChange,
  row,
  amount, setAmount,
  reason, setReason,
  onSubmit,
  onClear,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0F3622] border-2 border-[#F59E0B] shadow-[6px_6px_0px_0px_rgba(245,158,11,0.4)] sm:max-w-[520px]">
      <DialogHeader>
        <DialogTitle className="text-2xl font-bold text-white flex items-center gap-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
          <PencilSimple size={28} weight="duotone" className="text-[#F59E0B]" />
          Adjust Amount to Collect
        </DialogTitle>
        <DialogDescription className="sr-only">Override the consultancy charge for this candidate</DialogDescription>
      </DialogHeader>
      {row && (
        <div className="space-y-4">
          <div className="bg-[#0B2818] border-2 border-[#143E29] rounded-md p-3 text-sm">
            <p className="text-white font-bold">{row.candidate_name} <span className="text-white font-normal">– {row.company_name}</span></p>
            <p className="text-white mt-1">
              Status: <span className="font-bold">{row.joining_status}</span>
              {' · '}Original Charge: <span className="font-bold">{fmtInr(row.consultancy_charge)}</span>
              {' · '}Paid: <span className="font-bold">{fmtInr(row.total_paid)}</span>
            </p>
            {row.has_override && (
              <p className="text-white mt-1">
                Current Override: <span className="font-bold text-[#F59E0B]">{fmtInr(row.charge_override)}</span>
                {row.override_reason && <span className="ml-2">– {row.override_reason}</span>}
              </p>
            )}
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-white">New Amount to Collect (₹)</Label>
              <Input
                data-testid="adjust-amount-input"
                type="number" min="0" step="1" required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-white border-2 border-[#F59E0B] rounded-md px-3 py-2 text-black"
              />
              <p className="text-xs text-white mt-1">
                Replaces the original consultancy charge. Set <strong>0</strong> to clear all dues, or any other number (less or more than original).
              </p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-white">Reason / Note</Label>
              <Input
                data-testid="adjust-reason-input"
                type="text"
                placeholder="e.g. Fired in probation – partial refund agreed"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="bg-white border-2 border-[#F59E0B] rounded-md px-3 py-2 text-black"
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="submit"
                data-testid="submit-adjust"
                className="flex-1 bg-[#F59E0B] text-black border-2 border-[#F59E0B] rounded-md font-bold px-4 py-3 shadow-[3px_3px_0px_0px_rgba(245,158,11,0.5)] hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_rgba(245,158,11,0.7)] transition-all"
              >
                <CheckCircle size={20} weight="bold" className="mr-2" />
                Save Adjustment
              </Button>
              {row.has_override && (
                <Button
                  type="button"
                  data-testid="clear-adjust"
                  onClick={onClear}
                  className="bg-[#F87171] text-white border-2 border-black rounded-md font-bold px-3 py-3 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_0px_rgba(10,10,10,1)] transition-all"
                >
                  <ArrowCounterClockwise size={18} weight="bold" className="mr-1" />
                  Remove
                </Button>
              )}
            </div>
          </form>
        </div>
      )}
    </DialogContent>
  </Dialog>
);
